/**
 * OverallLayer
 * 
 * @Author: Focci
 * @Date: 2024-11-06 16:12:21
 * @Last Modified by: Focci
 * @Last Modified time: 2024-11-06 16:12:21
 */

import useResizeObserver from '@hook/useResizeObserver'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import clsx from 'clsx'
import SVGLayer from '@img/layer.svg'
import SVGLengend from '@img/lengend.svg'
import SVGClear from '@img/clear.svg'
import useApp from '@hook/useApp'
import { useMapState } from '@comp/mapbox'
import { useMap } from 'react-map-gl'
import { ButtonBase } from '@comp/material'
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { useTranslations } from 'next-intl'
import { isEqual, isFunction, map } from 'lodash-es'
import useMapboxOverall from './useMapboxOverall'
import OverallController from './controller'
import OverallLegends from './legends'
import TrafficPanel from './panel/TrafficPanel'
import FacilityPanel from './panel/FacilityPanel'
import MoveResearchCheckbox from '../panel/move-resesarch/MoveResearchCheckbox'

function OverallLayerScoffRef({
  title, 
  icon,
  // 自动关闭
  // 0 默认不展开，也不自动关闭；
  // 大于0 默认展开 autoCloseTime 时间后关闭
  autoCloseTime = 0,
  bodyHeight,
  onChange,
  onClear,
  ClearIcon = SVGClear,
  wrapperClassName = 'w-full md:w-52',
  children 
}, ref) {
  const timer = useRef()
  const [open, setOpen] = useState(!!autoCloseTime)

  const handleClick = useCallback(() => {
    const res = !open
    isFunction(onChange) && onChange(res)
    setOpen(res)
  }, [open, onChange])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { isFunction(onChange) && onChange(open) }, [])

  // 自动关闭
  useEffect(() => {
    const t = timer.current
    if(autoCloseTime > 0) {
      t && clearTimeout(t)
      timer.current = setTimeout(() => setOpen(false), autoCloseTime * 1000)
    }

    return () => { t && clearTimeout(t) }
  }, [autoCloseTime])

  useImperativeHandle(ref, () => ({
    tooggle: (v) => setOpen(v)
  }))

  if(!open) {
    return (
      <ButtonBase className="rounded-md" onClick={handleClick}>
        <div 
          className="
            shadow-[0px_2px_18px_0px_rgba(0,0,0,0.22)] rounded-md bg-white 
            p-1 min-w-[2.5rem] h-10 flex flex-col items-center justify-center
          "
        >
          {icon}
          <span className="line-clamp-1 text-f.6 -mb-1">{title}</span>
        </div>
      </ButtonBase>
    )
  }

  return (
    <div className={clsx(['shadow-[0px_2px_18px_0px_rgba(0,0,0,0.22)] rounded-md animate-fade-in', wrapperClassName])}>
      <div className="flex items-center gap-1 px-3 py-2 text-f.7 bg-white rounded-t-md">
        {icon}
        {title}
        <div className="ml-auto flex items-center gap-[.3rem]">
          {isFunction(onClear) && (
            <ButtonBase className="rounded-full p-0.5" onClick={onClear}>
              <ClearIcon />
            </ButtonBase>
          )}
          <ButtonBase 
            className={clsx(['rounded-full', open && 'rotate-180'])}
            onClick={handleClick}
          >
            <span 
              className="
                bg-black/80 rounded-full w-4 h-4 text-white flex 
                items-center justify-center
              "
            >
              <KeyboardArrowDownIcon className="size-3" />
            </span>
          </ButtonBase>
        </div>
      </div>
      <div
        style={{ maxHeight: `${bodyHeight}px` }}
        className={clsx([
          'custom-scroll-bar overscroll-contain bg-white rounded-b-md overflow-auto transition-all',
        ])}
      >
        {children}
      </div>
    </div>
  )
}

export const OverallLayerScoff = forwardRef(OverallLayerScoffRef)

export default function OverallLayer({
  type, 
  traffic, 
  facility,
  displayMoveResearch = false,
  ...props 
}) {
  const legendRef = useRef()
  const ts = useTranslations('search')
  const mps = useMapState()
  const { current } = useMap()
  const { isMobile } = useApp()
  const { height } = useResizeObserver(current?.getContainer?.())
  const { legends, clearOptions, ...option } = useMapboxOverall({ type })
  
  const [status, setStatus] = useState({ ctrl: false, legend: false })

  // 40 bottom & top 20
  // 50 header
  // 60 gap
  const rsize = height - 40 - (displayMoveResearch ? (50 + 50 + 60) : (50 + 60))

  const handleScoffChange = useCallback((prop) => (
    (v) => setStatus((prev) => ({ ...prev, [prop]: v }))
  ), [])

  const lastOptKeys = useRef()
  useEffect(() => {
    const optKeys = map(legends, 'key')

    if(!isEqual(optKeys, lastOptKeys.current) && legends.length > 0) {
      legendRef.current?.tooggle?.(true)
      setStatus((prev) => ({ ...prev, legend: true }))
    }

    lastOptKeys.current = optKeys
  }, [legends])

  return !isMobile && (
    <>
      <div className="absolute z-[6] left-4 top-4 flex flex-col items-start gap-4" {...props}>
        {/* 移动地图重新搜索 */}
        {displayMoveResearch && <MoveResearchCheckbox />}
        <OverallLayerScoff
          autoCloseTime={864000}
          title={ts('layerCategory')} 
          icon={<SVGLayer />} 
          bodyHeight={rsize * (status.legend ? 0.65 : 0.9)}
          onChange={handleScoffChange('ctrl')}
          onClear={clearOptions}
        >
          <OverallController 
            {...option}
            traffic={traffic} 
            facility={facility} 
          />
        </OverallLayerScoff>
        {legends.length > 0 && (
          <OverallLayerScoff
            autoCloseTime={120}
            icon={<SVGLengend />}
            ref={legendRef}
            title={ts('legend')} 
            bodyHeight={rsize * (status.ctrl ? 0.3 : 0.9)}
            onChange={handleScoffChange('legend')}
          >
            <OverallLegends legends={legends} />
          </OverallLayerScoff>
        )}
      </div>
      <div className="absolute z-[11] right-4 top-4">
        {traffic && mps.traffic && <TrafficPanel {...traffic} />}
        {facility && mps.facilityPanel && <FacilityPanel {...facility} />}
      </div>
    </>
  )
}
